export interface ExpenseAnomalyFilter {
  managerIds?: number[] | null
  userAccountIds?: number[] | null
  categoryIds?: number[] | null
  startDate?: string | null
  endDate?: string | null
  thresholdPercent?: number | null
  showOnlyAnomalies?: boolean
  search?: string | null
  pageNumber?: number
  pageSize?: number
}

export interface ExpenseAnomalySummary {
  usersWithAnomalies: number
  totalUsersInScope: number
  totalAnomaliesDetected: number
  totalAnomalyAmount: number
  totalSpendInPeriod: number
  anomalyAmountPercentOfTotal: number
  highestVariancePercent: number
  highestVarianceLabel: string
  categoriesWithAnomalies: number
  totalCategoriesInScope: number
  thresholdPercent: number
  minimumSpendAmount: number
}

export interface ExpenseAnomalyMonthCell {
  year: number
  month: number
  monthKey: string
  amount: number
  isAnomaly: boolean
  status?: string
  variancePercentage?: number | null
  ruleCode?: string
  explanation?: string
  severity?: string
}

export interface ExpenseAnomalyUserRow {
  userAccountId: number
  userName: string
  managerId?: number | null
  managerName?: string
  totalSpend: number
  anomaliesCount: number
  monthlySpend: ExpenseAnomalyMonthCell[]
  highestAnomalyCategory?: string
  highestAnomalyCategoryId?: number | null
  highestAnomalyChangePercent?: number | null
}

export interface ExpenseAnomalyUserList {
  items: ExpenseAnomalyUserRow[]
  totalRecords: number
  monthKeys: string[]
  summary?: ExpenseAnomalySummary
}

export interface ExpenseAnomalyUserHeader {
  userAccountId: number
  userName: string
  managerId?: number | null
  managerName?: string
  totalSpend: number
  anomaliesFound: number
}

export interface ExpenseAnomalyCategoryMonthCell {
  year: number
  month: number
  monthKey: string
  amount: number
  isAnomaly: boolean
  status?: string
  variancePercentage?: number | null
  ruleCode?: string
  explanation?: string
  severity?: string
}

export interface ExpenseAnomalyCategoryRow {
  expenseCategoryId: number
  categoryName: string
  months: ExpenseAnomalyCategoryMonthCell[]
  total: number
  anomaliesCount: number
}

export interface ExpenseAnomalyUserDetail {
  user: ExpenseAnomalyUserHeader
  monthKeys: string[]
  categories: ExpenseAnomalyCategoryRow[]
  totalsByMonth: ExpenseAnomalyCategoryMonthCell[]
  grandTotal: number
  totalAnomalies: number
}

export interface ExpenseAnomalyLineItem {
  expenseItemId: number
  lineItemDisplayId: string
  expenseDate: string
  description: string
  vendorName: string
  vendorId: number
  categoryName: string
  expenseCategoryId: number
  paymentMethodName: string
  amount: number
  receiptUrl?: string
  expenseId: number
  reportDisplayId: string
  source?: string
}

export interface ExpenseAnomalyLineItemsResult {
  userAccountId: number
  userName: string
  expenseCategoryId: number
  categoryName: string
  year: number
  month: number
  monthKey: string
  amount: number
  previousAmount: number
  changePercent?: number | null
  status?: string
  explanation?: string
  items: ExpenseAnomalyLineItem[]
  totalRecords: number
}

export interface ExpenseAnomalyLookupItem {
  id: number
  name: string
}

export interface ExpenseAnomalyLookups {
  managers: ExpenseAnomalyLookupItem[]
  users: ExpenseAnomalyLookupItem[]
  categories: ExpenseAnomalyLookupItem[]
  defaultThresholdPercent: number
  minimumSpendAmount: number
  isModuleEnabled: boolean
  isEnabledForManager: boolean
  scheduleFrequency?: string
  scheduleEmails?: string
}

export interface ExpenseAnomalySchedule {
  frequency: string
  emails: string
}

export type SelectOption = { value: number; label: string }

export const formatMoney = (n?: number | null) => {
  const v = Number(n ?? 0)
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

export const formatPct = (n?: number | null) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return ''
  const v = Number(n)
  const sign = v > 0 ? '+' : ''
  return `${sign}${v.toFixed(1)}%`
}

export const defaultLastMonthsRange = (months: number) => {
  const end = new Date()
  const start = new Date(end.getFullYear(), end.getMonth() - (months - 1), 1)
  const pad = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { startDate: pad(start), endDate: pad(end) }
}
